import { useI18n } from '@fox/extensions/src/use-i18n';
import Autocomplete from '@mui/material/Autocomplete';
import { FC, useCallback, useMemo, useState } from 'react';
import { DISABLE_BROWSER_AUTOFILL_SETTINGS } from '../../utils/disable-auto-complete';
import { MuiMenuItem } from '../mui-list/index';
import { TextField } from '../text-field/index';
import { useDebouncedPartialAddressSearch } from './hooks';
import type { IAddressPartAutoComplete } from './types';

type PartialAddressOption = {
	value: string;
	label: string;
};

/**
 * A specialized autocomplete input field for searching specific parts of an address.
 *
 * Supports searching for postcodes, localities (suburbs/cities), and states/provinces
 * using the Google Places API. Results are restricted to the current region based on
 * the i18n country code.
 *
 * **State Behavior:**
 * - Returns state abbreviations as values (e.g., "VIC", "NSW", "CA", "ON")
 * - Displays full state names in the dropdown (e.g., "Victoria", "New South Wales")
 * - Deduplicates results to show each state only once
 * - Works internationally for any country's administrative divisions
 *
 * **Postcode Behavior:**
 * - Returns the postcode number as the value
 * - Displays postcode with location context in the dropdown
 *
 * **Locality Behavior:**
 * - Returns the locality/suburb/city name as the value
 * - Displays locality with full address context in the dropdown
 *
 * @example
 * // Search for Australian states - returns abbreviations
 * <AddressPartAutoComplete
 *   addressPart="state"
 *   value={state}
 *   onChange={(value) => setState(value)} // value will be "VIC", "NSW", etc.
 *   label="State"
 *   placeholder="Search for a state..."
 * />
 *
 * @example
 * // Search for postcodes
 * <AddressPartAutoComplete
 *   addressPart="postcode"
 *   value={postcode}
 *   onChange={(value) => setPostcode(value)}
 *   label="Postcode"
 *   placeholder="Enter postcode..."
 * />
 *
 * @example
 * // Search for localities/suburbs
 * <AddressPartAutoComplete
 *   addressPart="locality"
 *   value={suburb}
 *   onChange={(value) => setSuburb(value)}
 *   label="Suburb"
 *   placeholder="Enter suburb..."
 * />
 */
export const AddressPartAutoComplete: FC<IAddressPartAutoComplete> = props => {
	const i18n = useI18n();
	const [search, setSearch] = useState(props.value ?? '');

	const [results, setResults, isFetching] = useDebouncedPartialAddressSearch(search, 500, props.addressPart, i18n.countryCode);

	// Transform API results into autocomplete options
	const options: PartialAddressOption[] = useMemo(
		() =>
			results.map(r => ({
				value: r.value,
				label: r.label
			})),
		[results]
	);

	// Find the current value in the options list (for controlled input)
	const currentOption = useMemo(() => options.find(o => o.value === props.value) ?? null, [options, props.value]);

	const handleChange = useCallback(
		(_: unknown, newVal: PartialAddressOption | string | null) => {
			if (typeof newVal === 'string') {
				// User typed free text without selecting from dropdown - update search only
				setSearch(newVal);
			} else if (newVal) {
				// User selected an option from the dropdown - update both value and search
				props.onChange?.(newVal.value);
				setSearch(newVal.value);
			} else {
				// User cleared the field
				props.onChange?.('');
				setSearch('');
			}
		},
		[props.onChange]
	);

	const handleInputChange = useCallback((_: unknown, val: string) => {
		// Update the search term as user types (for autocomplete filtering)
		setSearch(val);
	}, []);

	return (
		<div style={props.width ? { width: props.width } : {}}>
			<Autocomplete<PartialAddressOption, false, false, true>
				freeSolo
				openOnFocus
				loading={isFetching}
				renderInput={params => (
					<TextField
						{...params}
						size={props.size ?? 'small'}
						label={props.label}
						placeholder={props.placeholder}
						helperText={props.helperText}
						error={props.error}
					/>
				)}
				onChange={handleChange}
				onInputChange={handleInputChange}
				inputValue={search}
				value={currentOption}
				options={options}
				getOptionLabel={option => (typeof option === 'string' ? option : props.addressPart === 'state' ? option.label : option.value)}
				filterOptions={x => x}
				renderOption={(renderProps, option) => (
					<MuiMenuItem {...renderProps} key={option.value} value={option.value}>
						{props.addressPart === 'state' ? option.label : option.value}
					</MuiMenuItem>
				)}
				sx={{ ...DISABLE_BROWSER_AUTOFILL_SETTINGS }}
			/>
		</div>
	);
};
